import React, { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { qaApi } from "../../../services/api/qaApi";
import { FiCode, FiSend, FiX } from "react-icons/fi";
import "./AnswerForm.css";

const AnswerForm = ({ postId, onAnswerAdded, onCancel }) => {
  const [content, setContent] = useState("");
  const [code, setCode] = useState("");
  const [showCodeEditor, setShowCodeEditor] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!content.trim()) {
        alert("Please enter your answer");
        return;
      }

      if (content.trim().length < 30) {
        alert("Answer must be at least 30 characters long");
        return;
      }

      setSubmitting(true);
      try {
        const answerData = {
          content: content.trim(),
          code: code.trim() || "",
        };

        const response = await qaApi.postAnswer(postId, answerData);

        if (onAnswerAdded) {
          const newAnswer =
            response.data.answer || response.data.data || response.data;
          onAnswerAdded(newAnswer);
        }

        setContent("");
        setCode("");
        setShowCodeEditor(false);
        alert("Answer posted successfully!");
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to post answer. Please try again.";
        alert(errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
    [postId, content, code, onAnswerAdded]
  );

  const toggleCodeEditor = useCallback(() => {
    setShowCodeEditor((prev) => !prev);
  }, []);

  return (
    <motion.form
      onSubmit={handleSubmit}
      className="cbx-answer-form"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <h4 className="cbx-answer-form__title">Your Answer</h4>
      <p className="cbx-answer-form__hint">
        Be clear and detailed. Minimum 30 characters.
      </p>

      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Write your answer here... (minimum 30 characters)"
        rows={6}
        maxLength={10000}
        required
        className="cbx-answer-form__textarea"
      />

      <div className="cbx-answer-form__footer">
        <motion.button
          type="button"
          className="cbx-answer-form__toggle-code"
          onClick={toggleCodeEditor}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FiCode /> {showCodeEditor ? "Hide" : "Add"} Code
        </motion.button>

        <small
          className={`cbx-answer-form__char-count ${
            content.length < 30 ? "cbx-answer-form__char-count--warning" : ""
          }`}
        >
          {content.length}/10000 characters
          {content.length > 0 && content.length < 30 && (
            <span> (need {30 - content.length} more)</span>
          )}
        </small>
      </div>

      {/* Code Editor */}
      {showCodeEditor && (
        <motion.div
          className="cbx-answer-form__code-wrapper"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
        >
          <label className="cbx-answer-form__code-label">Code (optional)</label>
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Paste your code here..."
            rows={8}
            maxLength={5000}
            className="cbx-answer-form__code-editor"
          />
          <small className="cbx-answer-form__char-count">
            {code.length}/5000 characters
          </small>
        </motion.div>
      )}

      <div className="cbx-answer-form__actions">
        {onCancel && (
          <motion.button
            type="button"
            onClick={onCancel}
            className="cbx-answer-form__cancel-btn"
            disabled={submitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiX /> Cancel
          </motion.button>
        )}
        <motion.button
          type="submit"
          disabled={submitting || content.trim().length < 30}
          className="cbx-answer-form__submit-btn"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FiSend /> {submitting ? "Posting..." : "Post Your Answer"}
        </motion.button>
      </div>
    </motion.form>
  );
};

export default React.memo(AnswerForm);
